import { ThemeEvents } from '@theme/events';

const SELECTOR = '[data-beybling-free-ship]';

const DEFAULT_CURRENCY_DECIMALS = 2;

/** @type {Record<string, number>} */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

/**
 * @param {number} cents
 * @param {string} thousandsSeparator
 * @param {string} decimalSeparator
 * @param {number} precision
 */
function formatCentsValue(cents, thousandsSeparator, decimalSeparator, precision) {
  const roundedNumber = (cents / 100).toFixed(precision);
  let [whole = '0', fraction = ''] = roundedNumber.split('.');
  whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);
  return precision <= 0 ? whole : `${whole}${decimalSeparator}${fraction.padEnd(precision, '0')}`;
}

/**
 * Match Shopify Liquid `money` filter formatting.
 *
 * @param {number} cents
 * @param {string} [formatTemplate]
 * @param {string} [currencyCode]
 */
function formatShopifyMoney(cents, formatTemplate, currencyCode) {
  const template =
    formatTemplate || globalThis.Theme?.moneyFormat || elFallbackMoneyFormat || '${{amount}}';
  const currency = currencyCode || globalThis.Theme?.currency || 'USD';

  return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (typeof placeholder !== 'string') return '';

    if (placeholder === 'currency') {
      return currency;
    }

    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

    if (placeholder === 'amount_no_decimals') {
      precision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      thousandsSeparator = '.';
      precision = 0;
    } else if (placeholder === 'amount_no_decimals_with_space_separator') {
      thousandsSeparator = ' ';
      precision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'";
      decimalSeparator = '.';
    }

    return formatCentsValue(cents, thousandsSeparator, decimalSeparator, precision);
  });
}

/** @type {string | undefined} */
let elFallbackMoneyFormat;

/**
 * @param {HTMLElement} label
 */
function clearLabel(label) {
  label.replaceChildren();
}

/**
 * @param {HTMLElement} label
 * @param {string} before
 * @param {string} strongText
 * @param {string} [after]
 */
function setLabelWithStrong(label, before, strongText, after = '') {
  clearLabel(label);
  if (before) label.append(document.createTextNode(before));
  const strong = document.createElement('strong');
  strong.textContent = strongText;
  label.append(strong);
  if (after) label.append(document.createTextNode(after));
}

/**
 * @returns {SVGSVGElement}
 */
function createFreeShipTruckIcon(className = 'beybling-free-ship__icon') {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', `beybling-free-ship-truck ${className}`.trim());
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.75');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const shapes = [
    ['rect', { x: '1', y: '3', width: '15', height: '13' }],
    ['polygon', { points: '16 8 20 8 23 11 23 16 16 16 16 8' }],
    ['circle', { cx: '5.5', cy: '18.5', r: '2.5' }],
    ['circle', { cx: '18.5', cy: '18.5', r: '2.5' }],
  ];

  for (const [tag, attrs] of shapes) {
    const node = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [key, value] of Object.entries(attrs)) {
      node.setAttribute(key, value);
    }
    svg.append(node);
  }

  return svg;
}

/**
 * @param {HTMLElement} el
 */
function celebrateFreeShipTruck(el) {
  const icon = el.querySelector('.beybling-free-ship-truck');
  if (!(icon instanceof SVGElement)) return;

  icon.classList.remove('beybling-free-ship-truck--celebrate');
  void icon.offsetWidth;
  icon.classList.add('beybling-free-ship-truck--celebrate');
}

/**
 * @param {HTMLElement} label
 */
function setUnlockedDrawerLabel(label) {
  clearLabel(label);
  label.append(createFreeShipTruckIcon(), document.createTextNode(' You\u2019ve unlocked '));
  const strong = document.createElement('strong');
  strong.textContent = 'free shipping';
  label.append(strong, document.createTextNode('!'));
}

/**
 * @param {HTMLElement} el
 * @param {string} currencyCode
 */
function renderIdleBannerLabel(el) {
  const label = el.querySelector('[data-beybling-free-ship-label]');
  const thresholdMoney = el.dataset.thresholdMoney;
  if (!(label instanceof HTMLElement) || !thresholdMoney) return;

  setLabelWithStrong(label, 'Free shipping on orders over ', thresholdMoney);
}

/**
 * @param {HTMLElement} el
 * @param {{ item_count: number, total_price: number, currency?: string }} cart
 * @param {number} thresholdCents
 * @param {{ updateLabel?: boolean }} [options]
 */
function renderFreeShipping(el, cart, thresholdCents, options = {}) {
  const { updateLabel = true } = options;
  const label = el.querySelector('[data-beybling-free-ship-label]');
  const fill = el.querySelector('[data-beybling-free-ship-fill]');
  const track = el.querySelector('[data-beybling-free-ship-track]');
  const variant = el.dataset.variant || 'banner';
  const currency = cart.currency || globalThis.Theme?.currency || 'USD';

  if (!cart.item_count) {
    el.classList.remove(
      'beybling-free-ship--unlocked',
      'beybling-free-ship-banner--unlocked',
      'beybling-free-ship-banner--has-progress'
    );

    if (variant === 'drawer') {
      el.hidden = true;
      el.classList.add('beybling-free-ship--idle');
      return;
    }

    if (track instanceof HTMLElement) track.hidden = true;

    if (updateLabel) {
      renderIdleBannerLabel(el);
    }

    el.dataset.cartCount = '0';
    return;
  }

  if (variant === 'drawer') {
    el.hidden = false;
    el.classList.remove('beybling-free-ship--idle');
  }

  const remaining = thresholdCents - cart.total_price;
  const unlocked = remaining <= 0;
  const wasUnlocked = el.classList.contains('beybling-free-ship--unlocked') ||
    el.classList.contains('beybling-free-ship-banner--unlocked');
  const pct = unlocked ? 100 : Math.min(100, Math.floor((cart.total_price * 100) / thresholdCents));

  el.classList.toggle('beybling-free-ship--unlocked', unlocked);
  el.classList.toggle('beybling-free-ship-banner--unlocked', unlocked);
  el.classList.toggle('beybling-free-ship-banner--has-progress', true);

  if (track instanceof HTMLElement) track.hidden = false;
  if (fill instanceof HTMLElement) fill.style.width = `${pct}%`;

  if (!updateLabel || !(label instanceof HTMLElement)) {
    el.dataset.cartCount = String(cart.item_count);
    return;
  }

  if (unlocked) {
    if (variant === 'drawer') {
      setUnlockedDrawerLabel(label);
    } else {
      setLabelWithStrong(label, 'You\u2019ve unlocked ', 'free shipping');
    }

    if (!wasUnlocked) {
      celebrateFreeShipTruck(el);
      document.dispatchEvent(new CustomEvent('beybling:free-shipping-unlocked', { bubbles: true }));
    }

    el.dataset.cartCount = String(cart.item_count);
    return;
  }

  const remainingMoney = formatShopifyMoney(Math.max(remaining, 0), el.dataset.moneyFormat, currency);

  if (variant === 'drawer') {
    setLabelWithStrong(label, 'You\u2019re ', remainingMoney, ' away from free shipping');
  } else {
    setLabelWithStrong(label, '', remainingMoney, ' away from free shipping');
  }

  el.dataset.cartCount = String(cart.item_count);
}

/**
 * @param {HTMLElement} el
 * @param {{ item_count: number, total_price: number, currency?: string }} cart
 * @param {number} thresholdCents
 */
function needsLabelUpdate(el, cart, thresholdCents) {
  const serverCount = parseInt(el.dataset.cartCount || '0', 10);
  if (cart.item_count !== serverCount) return true;
  if (!cart.item_count) return false;

  const remaining = thresholdCents - cart.total_price;
  const unlocked = remaining <= 0;
  const label = el.querySelector('[data-beybling-free-ship-label]');
  if (!(label instanceof HTMLElement)) return true;

  if (unlocked) {
    return !label.textContent?.includes('unlocked');
  }

  const remainingMoney = formatShopifyMoney(
    Math.max(remaining, 0),
    el.dataset.moneyFormat,
    cart.currency || globalThis.Theme?.currency || 'USD'
  );

  return !label.textContent?.includes(remainingMoney);
}

/**
 * @param {Event | undefined} event
 * @returns {{ item_count: number, total_price: number, currency?: string } | null}
 */
function cartFromEvent(event) {
  const resource = event?.detail?.resource;
  if (!resource || typeof resource !== 'object') return null;
  if (typeof resource.item_count !== 'number' || typeof resource.total_price !== 'number') return null;

  return {
    item_count: resource.item_count,
    total_price: resource.total_price,
    currency: resource.currency || globalThis.Theme?.currency,
  };
}

/**
 * Shopify Markets: the threshold is stored in the store currency. Convert it to the
 * shopper's currency with the same rate Shopify uses for shipping-rate conditions,
 * rounded up to a whole unit so we never promise free shipping below the real rate.
 *
 * @param {HTMLElement} el
 * @returns {boolean} true when the threshold was converted on this call
 */
function localizeThreshold(el) {
  if (el.dataset.thresholdLocalized === 'true') return false;
  el.dataset.thresholdLocalized = 'true';

  const shopCurrency = el.dataset.shopCurrency;
  const active = globalThis.Shopify?.currency?.active;
  const rate = parseFloat(globalThis.Shopify?.currency?.rate || '');
  if (!shopCurrency || !active || active === shopCurrency || !(rate > 0)) return false;

  const base = parseInt(el.dataset.thresholdCents || '0', 10);
  if (base <= 0) return false;

  // Round UP to a clean number (e.g. CA$146 -> CA$150). Always at or above the real converted threshold.
  const units = Math.ceil((base * rate) / 100);
  const step = units >= 100 ? 10 ** (Math.floor(Math.log10(units)) - 1) : 5;
  const converted = Math.ceil(units / step) * step * 100;
  el.dataset.thresholdCents = String(converted);
  el.dataset.thresholdMoney = formatShopifyMoney(converted, el.dataset.moneyFormat, active);
  return true;
}

/**
 * Labels rendered in a foreign currency start hidden (server can't convert); show once correct.
 *
 * @param {HTMLElement} el
 */
function revealLabel(el) {
  const label = el.querySelector('[data-beybling-free-ship-label]');
  if (label instanceof HTMLElement) label.style.removeProperty('visibility');
}

/**
 * @param {{ item_count: number, total_price: number, currency?: string }} cart
 * @param {{ forceLabel?: boolean }} [options]
 */
function applyFreeShipping(cart, options = {}) {
  const elements = document.querySelectorAll(SELECTOR);
  if (!elements.length) return;

  elements.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const localized = localizeThreshold(el);
    const threshold = parseInt(el.dataset.thresholdCents || '0', 10);
    if (threshold <= 0) return;

    const updateLabel = options.forceLabel === true || localized || needsLabelUpdate(el, cart, threshold);
    renderFreeShipping(el, cart, threshold, { updateLabel });
    revealLabel(el);
  });
}

/**
 * @param {{ forceLabel?: boolean, cart?: { item_count: number, total_price: number, currency?: string } }} [options]
 */
async function syncFreeShipping(options = {}) {
  const elements = document.querySelectorAll(SELECTOR);
  if (!elements.length) return;

  const first = elements[0];
  if (first instanceof HTMLElement && first.dataset.moneyFormat) {
    elFallbackMoneyFormat = first.dataset.moneyFormat;
  }

  elements.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (localizeThreshold(el) && el.dataset.cartCount === '0' && el.dataset.variant !== 'drawer') {
      renderIdleBannerLabel(el);
      revealLabel(el);
    }
  });

  let cart = options.cart;
  if (cart) {
    applyFreeShipping(cart, options);
    return;
  }

  const cartUrl = globalThis.Theme?.routes?.cart_js_url || '/cart.js';

  try {
    const response = await fetch(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) return;

    cart = await response.json();
    applyFreeShipping(cart, options);
  } catch {
    /* cart.js unavailable — keep server-rendered markup */
  }
}

/**
 * @param {Event} [event]
 */
function scheduleFreeShippingSync(event) {
  requestAnimationFrame(() => {
    const cart = cartFromEvent(event);
    syncFreeShipping({ forceLabel: true, cart: cart || undefined });
  });
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleFreeShippingSync);
document.addEventListener(ThemeEvents.discountUpdate, scheduleFreeShippingSync);
document.addEventListener('cart:update', scheduleFreeShippingSync);
document.addEventListener('beybling:cart-drawer-reflow', scheduleFreeShippingSync);
document.addEventListener('shopify:section:load', scheduleFreeShippingSync);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    syncFreeShipping();
    document.querySelectorAll(`${SELECTOR}.beybling-free-ship-banner--unlocked, ${SELECTOR}.beybling-free-ship--unlocked`).forEach((el) => {
      if (el instanceof HTMLElement) celebrateFreeShipTruck(el);
    });
  }, { once: true });
} else {
  syncFreeShipping();
  document.querySelectorAll(`${SELECTOR}.beybling-free-ship-banner--unlocked, ${SELECTOR}.beybling-free-ship--unlocked`).forEach((el) => {
    if (el instanceof HTMLElement) celebrateFreeShipTruck(el);
  });
}
